import { supabase } from '@/integrations/supabase/client';
import { VendorSetupData } from '@/contracts/vendor-setup';
import { VendorApplicationRow } from '@/contracts/vendor-application';

export async function getApplicationForSetup(applicationId: string): Promise<VendorApplicationRow | null> {
  const { data, error } = await supabase
    .from('vendor_applications')
    .select('*')
    .eq('id', applicationId)
    .single();
  
  if (error) {
    console.error('Error fetching application:', error);
    return null;
  }
  
  return data as VendorApplicationRow;
}

export async function createProductFromApplication(
  applicationId: string,
  setupData: VendorSetupData
): Promise<string> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error('Not authenticated');

  // Generate slug from title
  const slug = setupData.details.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') + '-' + Date.now().toString(36);

  // Create the product
  const { data: product, error: productError } = await supabase
    .from('products')
    .insert({
      slug,
      title: setupData.details.title,
      subtitle: setupData.details.subtitle,
      description: setupData.details.description,
      categories: setupData.details.categories,
      best_for: setupData.details.best_for,
      service_type: setupData.details.service_type,
      image: setupData.media.hero_image_url || setupData.media.logo_url,
      profile_image: setupData.media.logo_url,
      gallery: setupData.media.gallery,
      demo_video_url: setupData.media.demo_video_url,
      price: setupData.pricing.price,
      msrp: setupData.pricing.msrp,
      price_duration: setupData.pricing.price_duration,
      features: setupData.features.features,
      feature_highlights: setupData.features.feature_highlights,
      at_a_glance: setupData.features.at_a_glance,
      testimonial_videos: setupData.socialProof.testimonial_videos,
      roi_metrics: setupData.socialProof.roi_metrics,
      status: 'pending_review',
      is_published: false,
      vendor_id: user.user.id,
      created_by: user.user.id,
    } as any)
    .select('id')
    .single();

  if (productError) throw productError;

  // Create product plans if any
  if (setupData.pricing.plans.length > 0) {
    const { error: plansError } = await supabase
      .from('product_plans')
      .insert(
        setupData.pricing.plans.map((plan, index) => ({
          product_id: product.id,
          name: plan.name,
          price: plan.price,
          original_price: plan.original_price,
          features: plan.features,
          setup_fee: plan.setup_fee,
          sort_order: index,
        }))
      );

    if (plansError) console.error('Error creating plans:', plansError);
  }

  // Link product to application
  await supabase
    .from('vendor_applications')
    .update({ product_id: product.id } as any)
    .eq('id', applicationId);

  return product.id;
}

export async function updateVendorProduct(
  productId: string,
  setupData: Partial<VendorSetupData>
): Promise<void> {
  const updates: Record<string, any> = {};

  if (setupData.media) {
    if (setupData.media.hero_image_url) updates.image = setupData.media.hero_image_url;
    if (setupData.media.logo_url) updates.profile_image = setupData.media.logo_url;
    if (setupData.media.gallery) updates.gallery = setupData.media.gallery;
    if (setupData.media.demo_video_url) updates.demo_video_url = setupData.media.demo_video_url;
  }

  if (setupData.details) {
    updates.title = setupData.details.title;
    updates.subtitle = setupData.details.subtitle;
    updates.description = setupData.details.description;
    updates.categories = setupData.details.categories;
    updates.best_for = setupData.details.best_for;
    updates.service_type = setupData.details.service_type;
  }

  if (setupData.pricing) {
    updates.price = setupData.pricing.price;
    updates.msrp = setupData.pricing.msrp;
    updates.price_duration = setupData.pricing.price_duration;
  }

  if (setupData.features) {
    updates.features = setupData.features.features;
    updates.feature_highlights = setupData.features.feature_highlights;
    updates.at_a_glance = setupData.features.at_a_glance;
  }

  if (setupData.socialProof) {
    updates.testimonial_videos = setupData.socialProof.testimonial_videos;
    updates.roi_metrics = setupData.socialProof.roi_metrics;
  }

  const { error } = await supabase
    .from('products')
    .update(updates)
    .eq('id', productId);

  if (error) throw error;
}
